import { FC, Children, isValidElement, ReactNode, useCallback, useState, useEffect, useContext, useRef } from "react";
import ReactSelect, { SingleValue, MultiValue, ActionMeta, components } from "react-select";
import cn from "classnames";

import { ReactSelectProps, SelectOption, SelectChangeResult } from "./Select.props";
import styles from "./Select.module.scss";
import { FormContext } from "../../Form";

/**
 * @deprecated
 */
const ReactSelectComponent: FC<ReactSelectProps> = ({
  id,
  children,
  field,
  disabled,
  isMulti,
  placeholder,
  onChange,
  onInputChange,
  tabIndex,
  labelText,
  isInvalid,
  inputExtraClass,
  isClearable,
  name,
}: ReactSelectProps): JSX.Element => {
  const { getValues } = useContext(FormContext);
  const [selectedValue, setSelectedValue] = useState<SingleValue<SelectOption> | MultiValue<SelectOption>>(null);
  const { ...otherFieldValues } = field;

  let fieldValue: unknown = undefined;
  if (getValues) {
    fieldValue = getValues(name);
  }

  /** loops through the children options and create the json react-select needs to add options */
  const selectOptions = Children.map(children, (child: ReactNode) => {
    if (isValidElement(child) && child.props.children && child.props.value !== undefined) {
      return { value: child.props.value, label: child.props.children } as SelectOption;
    } else {
      return null;
    }
  });

  const selectOptionsRef = useRef<ReadonlyArray<SelectOption> | null | undefined>(selectOptions);
  selectOptionsRef.current = selectOptions;

  const handleSelectOnChange = useCallback(
    (selected: SingleValue<SelectOption> | MultiValue<SelectOption>, action: ActionMeta<SelectOption>): void => {
      let results: SelectChangeResult;

      // Pass out the values if they're specified.
      if (selected === null) {
        results = null;
      } else if (Array.isArray(selected)) {
        if (selected.length === 0) results = null;
        else results = (selected as MultiValue<SelectOption>).map((option) => option.value);
      } else {
        results = (selected as SelectOption).value;
      }

      onChange && onChange(results, action);
      field.onChange(results);
      setSelectedValue(selected);
    },
    [onChange],
  );

  useEffect(() => {
    if (fieldValue === undefined || fieldValue === null) {
      setSelectedValue(null);
    } else {
      const options = selectOptionsRef.current;
      if (!options) {
        setSelectedValue(null);
        return;
      }

      let selectedValue: SingleValue<SelectOption> | MultiValue<SelectOption>;

      if (Array.isArray(fieldValue)) {
        const fieldSet = new Set(fieldValue);
        // eslint-disable-next-line
        selectedValue = options.filter((option: any) => fieldSet.has(option.value));
      } else {
        // eslint-disable-next-line
        selectedValue = options.find((option: any) => option.value === fieldValue) || null;
      }

      setSelectedValue(selectedValue);
    }
  }, [fieldValue]);

  const selectClass = cn(styles.select, inputExtraClass);

  return (
    <ReactSelect
      {...otherFieldValues}
      placeholder={placeholder}
      tabIndex={tabIndex}
      id={id}
      className={selectClass}
      classNamePrefix="caprx"
      inputId={`${id}-input-select`}
      instanceId={id}
      components={{
        IndicatorSeparator: (): null => null,
        MenuList: ({ className, ...rest }) => (<components.MenuList className={cn("items", { "opened": rest.selectProps.menuIsOpen }, className)} {...rest} />),
        Option: ({ className, ...rest }) => (<components.Option className={cn("option", "content", { "focused": rest.isFocused }, className)} {...rest} />),
      }}
      aria-labelledby={id}
      aria-label={labelText}
      aria-invalid={isInvalid ? true : false}
      isDisabled={disabled}
      isSearchable
      isClearable={isClearable}
      isMulti={isMulti}
      onChange={handleSelectOnChange}
      onInputChange={onInputChange}
      options={selectOptions ? selectOptions : []}
      value={selectedValue}
      menuPlacement="auto"
    />
  );
};

/**
 * @deprecated
 */
export default ReactSelectComponent;
